/**
 * TaskBoard Component
 * Single Responsibility: Display and manage meeting tasks
 */

import React from 'react'
import { Clock } from 'lucide-react'
import { Task } from '../../../types/meeting'

interface MeetingTaskBoardProps {
  tasks: Task[]
  onAddTask: (task: Omit<Task, 'id' | 'status'>) => void
  onOpenTaskModal: () => void
}

const MeetingTaskBoard: React.FC<MeetingTaskBoardProps> = ({
  tasks,
  onAddTask: _onAddTask,
  onOpenTaskModal
}) => {
  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200'
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200'
    }
  }

  const formatDueDate = (dateStr: string | null | undefined) => {
    // Handle null or undefined dates
    if (!dateStr) return 'No due date'

    const date = new Date(dateStr)
    // Check for invalid date
    if (isNaN(date.getTime())) return 'No due date'

    const today = new Date()
    const diffTime = date.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200">
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-kielis-navy">
          📋 Follow-up Tasks
        </h3>
        <button
          onClick={onOpenTaskModal}
          className="px-3 py-1.5 bg-kielis-navy text-white rounded-lg text-sm hover:bg-slate-800 transition-all"
        >
          + Add Task
        </button>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <div className="text-4xl mb-2">📭</div>
          <p>No tasks yet. Add your first task to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all"
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={task.status === 'completed'}
                  className="w-4 h-4 text-kielis-navy border-slate-300 rounded focus:ring-2 focus:ring-kielis-gold"
                  onChange={() => {
                    // In production, this would update task status
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-slate-800">
                      {/* Clean up task title - remove "Follow up on:" prefix if present */}
                      {task.title.replace(/^Follow up on:\s*/i, '').trim()}
                    </div>
                    {/* Task type badge */}
                    {task.task_type && (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                        {task.task_type.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  {task.description && task.description !== task.title && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {task.description.replace(/^Follow up on:\s*/i, '').trim()}
                    </div>
                  )}
                  {/* Estimated duration */}
                  {task.estimated_duration && (
                    <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                      <Clock className="w-3 h-3" />
                      <span>{task.estimated_duration}</span>
                    </div>
                  )}
                  {/* Prerequisites */}
                  {task.prerequisites && task.prerequisites.length > 0 && (
                    <div className="text-xs text-slate-400 mt-1">
                      <span className="font-medium">Prerequisites:</span>{' '}
                      {task.prerequisites.join(', ')}
                    </div>
                  )}
                  {/* Confidence score */}
                  {task.confidence_score !== undefined && task.confidence_score > 0 && (
                    <div className="text-xs text-slate-400 mt-1">
                      Confidence: {Math.round(task.confidence_score * 100)}%
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 text-xs rounded-full border ${getPriorityColor(
                    task.priority
                  )}`}
                >
                  {task.priority}
                </span>
                <div className="text-xs text-slate-500">
                  <div className="font-medium">{task.owner}</div>
                  <div>{formatDueDate(task.dueDate)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between text-xs text-slate-500">
        <div>
          {tasks.filter((t) => t.status === 'pending').length} pending •{' '}
          {tasks.filter((t) => t.status === 'completed').length} completed
        </div>
        <button className="text-kielis-navy hover:underline">
          View all tasks →
        </button>
      </div>
    </div>
  )
}

export default MeetingTaskBoard